/**
 * Enumerations for the experiment learnings: the AI layer over concluded tests
 * (docs/frontend/experiment-learnings.md).
 *
 * The tuples become PostgreSQL enums in pertento_db/schema/learnings.ts, so a value is
 * added here first. Renaming one later is an enum migration plus a re-label of every
 * card that carries it; adding one is cheap.
 */
import type { REPORT_OUTSIDE_FACTORS, TRACKED_DEVICE_TYPES } from './index';

export type ReportOutsideFactor = (typeof REPORT_OUTSIDE_FACTORS)[number];

/** What a variant changed. A variant can carry several, so it is stored as jsonb and validated in code. */
export const LEARNING_CHANGE_TYPES = [
  'copy',
  'layout',
  'visual-design',
  'social-proof',
  'urgency',
  'pricing',
  'promotion',
  'form',
  'navigation',
  'product-information',
  'delivery-returns',
  'trust',
  'media',
  'personalisation',
  'removal',
  'other',
] as const;

export const LEARNING_PAGE_TYPES = [
  'home',
  'plp',
  'pdp',
  'cart',
  'checkout',
  'search',
  'landing',
  'account',
  'content',
  'sitewide',
  'other',
] as const;

/** The broad, fixed level of an element. The specific name is free text per website. */
export const LEARNING_ELEMENT_CATEGORIES = [
  'cta',
  'headline',
  'price',
  'product-images',
  'navigation',
  'form',
  'social-proof',
  'delivery-info',
  'promo-banner',
  'other',
] as const;

export const HYPOTHESIS_VERDICTS = ['supported', 'contradicted', 'says-nothing'] as const;

/**
 * How a card came to be. 'refused' and 'failed' are claims without content, so a test
 * the model will not or cannot label is not retried every tick. 'confirmed' and 'edited'
 * join this list when human review arrives.
 */
export const LEARNING_INSIGHT_STATUSES = ['generated', 'refused', 'failed'] as const;

/** Not a DB enum: computed from a pooled lift's band. Here so server and dashboard share one union. */
export const LEARNING_TIERS = ['consistent', 'early-signal', 'small-effect', 'not-enough-evidence', 'mixed'] as const;

/**
 * Whether an account's cards may feed the global intelligence engine. The terms make
 * this opt-out, so the default is to contribute. An enum rather than a boolean so a
 * third state can be added without a migration of meaning.
 */
export const LEARNINGS_CONTRIBUTIONS = ['contribute', 'opted-out'] as const;

/**
 * Debrief outside factors that exclude a test from patterns outright: the measurement, or
 * the page measured, was not the one the variant was tested on. The same treatment a broken
 * traffic split gets.
 */
export const EXCLUDING_OUTSIDE_FACTORS = [
  'tracking-issue',
  'site-release',
] as const satisfies readonly ReportOutsideFactor[];

/**
 * Every other outside factor widens the test's band instead of dropping it: its standard
 * error is multiplied once, however many factors are ticked, because people tend to tick
 * everything that might apply. 1.5 is a judgement, not a measurement; revisit it once real
 * debriefs exist (docs/frontend/experiment-learnings.md, "Debriefs").
 */
export const CONFOUNDER_SE_MULTIPLIER = 1.5;

/**
 * A concluded test is not read until it has been over this long. Its final statistics are
 * written by a different cron a few minutes behind, and a card made in that gap would read
 * the test as having no data. The cron waits this long, and the Library's "writing
 * learnings" count leaves a test out until then, so the two agree on what is pending.
 */
export const LEARNINGS_SETTLE_MS = 60 * 60 * 1000;

/**
 * A device a learning can be about. A test that targeted no device, or 'All', ran on every one and
 * is listed with none: it belongs to every device's view.
 */
export type LearningDevice = (typeof TRACKED_DEVICE_TYPES)[number];

export type ConfounderEffect = 'exclude' | 'widen' | 'none';

/** What the team's reported outside factors do to a test in patterns. An excluding factor wins. */
export const confounderEffect = (factors: readonly ReportOutsideFactor[]): ConfounderEffect => {
  if (factors.some((factor) => (EXCLUDING_OUTSIDE_FACTORS as readonly string[]).includes(factor))) return 'exclude';
  return factors.length > 0 ? 'widen' : 'none';
};

/**
 * Where an idea came from. Every source but 'untested-area' and 'customer-voice' needs
 * experiment evidence, so an idea citing none is dropped in code. 'debrief-next-step' is a
 * test whose team chose retest or iterate in its debrief; the idea cites the team's note,
 * labelled as the team's.
 */
export const EXPERIMENT_IDEA_SOURCES = [
  'extend-winner',
  'iterate-winner',
  'retest-maybe',
  'untested-area',
  'customer-voice',
  'flip-loss',
  'debrief-next-step',
] as const;

export const EXPERIMENT_IDEA_STATUSES = ['open', 'dismissed', 'drafted', 'retired'] as const;

export type ExperimentIdeaSource = (typeof EXPERIMENT_IDEA_SOURCES)[number];
export type ExperimentIdeaStatus = (typeof EXPERIMENT_IDEA_STATUSES)[number];

/** The sources that must cite at least one experiment. */
export const EXPERIMENT_IDEA_EVIDENCE_SOURCES = [
  'extend-winner',
  'iterate-winner',
  'retest-maybe',
  'flip-loss',
  'debrief-next-step',
] as const satisfies readonly ExperimentIdeaSource[];

export const EXPERIMENT_IDEA_SOURCE_LABELS: Record<ExperimentIdeaSource, string> = {
  'extend-winner': 'Extend a winner',
  'iterate-winner': 'Iterate on a winner',
  'retest-maybe': 'Retest a promising result',
  'untested-area': 'Untested area',
  'customer-voice': 'From survey comments',
  'flip-loss': 'Learn from a loss',
  'debrief-next-step': 'Next step from the team',
};

/**
 * What an opportunity on the Learnings page asks, decided 2026-10-20 (they replace Ideas). Each is
 * found in code from the website's evidence; the model only words it.
 *   explain-surprise  a result nothing predicted: why did it happen?
 *   replicate         a positive lean the range cannot yet tell from zero: does it hold up?
 *   resolve-mixed     tests of one thing that disagree in direction: which version helped?
 *   isolate           one test that changed several things at once: which part did it?
 *   fill-gap          change types a much-tested page has never tried, though tried elsewhere
 */
export const OPPORTUNITY_KINDS = ['explain-surprise', 'replicate', 'resolve-mixed', 'isolate', 'fill-gap'] as const;

/**
 * An opportunity's life. Open until someone dismisses it or drafts an experiment from it; retired
 * by the system when the evidence no longer raises the question (the finder stopped producing it).
 * A dismissed one stays dismissed while the question is the same.
 */
export const OPPORTUNITY_STATUSES = ['open', 'dismissed', 'drafted', 'retired'] as const;

/** What a team said about a pattern on the Learnings page: they knew it already, or it does not help. */
export const PATTERN_FEEDBACK = ['known', 'not-useful'] as const;

/**
 * The live figures a model-written sentence may refer to, filled in by code when the page is read.
 * The model never writes a number: a digit outside a placeholder rejects the sentence.
 */
export const WORDING_PLACEHOLDERS = ['lift', 'lower', 'upper', 'tests'] as const;

/** Generations per website per rolling 24 hours, counted from idea_batches so a restart does not reset it. */
export const IDEA_GENERATIONS_PER_DAY = 3;

/**
 * The `feature` learnings spend AI credits under (ai_credit_spends.feature), and the prefix of
 * every charge's ref. Decided 2026-10-19: learnings count towards the account's AI use, like
 * survey sentiment and the editor AI.
 */
export const LEARNINGS_CREDIT_FEATURE = 'learnings';

/** The model calls learnings make, one usage row each (learnings_ai_usage.kind). */
export const LEARNINGS_AI_CALL_KINDS = ['card', 'themes', 'ask', 'ideas', 'wording'] as const;

/**
 * Why learnings are not spending right now, for the Library and the ask and ideas refusals: an
 * account on credits that has run out, one not on credits that reached its monthly cap, or a
 * website with no account to pay at all.
 */
export const LEARNINGS_PAUSES = ['out-of-credits', 'monthly-cap', 'no-account'] as const;

/** What an idea cites. Ids are checked against the model's input in code, never trusted. */
export type IdeaEvidence = {
  experimentIds: number[];
  variantIds: number[];
  themeIds: number[];
  pageTypes: LearningPageType[];
  surveyResponseIds: number[];
  /** Experiments whose team note the idea rests on (debrief-next-step); shown labelled as the team's. */
  teamNoteExperimentIds: number[];
};

export type LearningChangeType = (typeof LEARNING_CHANGE_TYPES)[number];
export type LearningPageType = (typeof LEARNING_PAGE_TYPES)[number];
export type LearningElementCategory = (typeof LEARNING_ELEMENT_CATEGORIES)[number];
export type HypothesisVerdict = (typeof HYPOTHESIS_VERDICTS)[number];
export type LearningInsightStatus = (typeof LEARNING_INSIGHT_STATUSES)[number];
export type LearningTier = (typeof LEARNING_TIERS)[number];
export type LearningsContribution = (typeof LEARNINGS_CONTRIBUTIONS)[number];
export type LearningsAiCallKind = (typeof LEARNINGS_AI_CALL_KINDS)[number];
export type LearningsPause = (typeof LEARNINGS_PAUSES)[number];
export type OpportunityKind = (typeof OPPORTUNITY_KINDS)[number];

/** What each kind is called, on the Learnings page and to the AI chats that read its opportunities. */
export const OPPORTUNITY_KIND_LABELS: Record<OpportunityKind, string> = {
  'explain-surprise': 'Explain a surprise',
  replicate: 'Replicate',
  'resolve-mixed': 'Resolve mixed',
  isolate: 'Isolate',
  'fill-gap': 'Fill a gap',
};
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];
export type PatternFeedback = (typeof PATTERN_FEEDBACK)[number];
export type WordingPlaceholder = (typeof WORDING_PLACEHOLDERS)[number];

export const LEARNING_CHANGE_TYPE_LABELS: Record<LearningChangeType, string> = {
  copy: 'Copy',
  layout: 'Layout',
  'visual-design': 'Visual design',
  'social-proof': 'Social proof',
  urgency: 'Urgency',
  pricing: 'Pricing',
  promotion: 'Promotion',
  form: 'Form',
  navigation: 'Navigation',
  'product-information': 'Product information',
  'delivery-returns': 'Delivery and returns',
  trust: 'Trust',
  media: 'Media',
  personalisation: 'Personalisation',
  removal: 'Removal',
  other: 'Other',
};

export const LEARNING_PAGE_TYPE_LABELS: Record<LearningPageType, string> = {
  home: 'Home',
  plp: 'Category page',
  pdp: 'Product page',
  cart: 'Cart',
  checkout: 'Checkout',
  search: 'Search',
  landing: 'Landing page',
  account: 'Account',
  content: 'Content',
  sitewide: 'Sitewide',
  other: 'Other',
};

export const LEARNING_ELEMENT_CATEGORY_LABELS: Record<LearningElementCategory, string> = {
  cta: 'Call to action',
  headline: 'Headline',
  price: 'Price',
  'product-images': 'Product images',
  navigation: 'Navigation',
  form: 'Form',
  'social-proof': 'Social proof',
  'delivery-info': 'Delivery information',
  'promo-banner': 'Promo banner',
  other: 'Other',
};

/**
 * How far a concluded test's result can be trusted. Computed in code at generation,
 * never by the model, and stored on the card so the learning sentence and the flags it
 * was written under can never disagree.
 */
export type EvidenceFlags = {
  /** Sample ratio mismatch on the randomised arms. null when it cannot be judged, including any test with a deployed variant. */
  srm: boolean | null;
  /** Some randomised arm under the minimum sessions, or under the saved plan's required sample. */
  underpowered: boolean;
  /** Share of the plan's required sample the smallest arm reached, 0..1, or null with no usable plan. */
  planShare: number | null;
  /** Ran under seven days, so it never covered a full weekly cycle. null when the start is unknown. */
  shortRun: boolean | null;
  /** Another experiment that overlapped in time and collided on the page, or null. */
  interaction: { otherExperimentId: number; kind: string } | null;
  /** No stored statistics at all. */
  noData: boolean;
  /**
   * What the team ticked in the test's debrief as going on outside the test. [] when there is
   * no debrief or nothing was ticked: absence is never evidence that nothing interfered.
   */
  outsideFactors: ReportOutsideFactor[];
  /** A debrief with at least one answer exists. Separates "no debrief" from "no factors ticked". */
  hasDebrief: boolean;
};
